import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text, Image } from 'rebass';
import { FaChevronRight } from 'react-icons/fa';
import { FiChevronLeft, FiX } from 'react-icons/fi';
import ReactPlayer from 'react-player';

import Loading from 'app/loading';
import { FT_COLORS } from '../theme';
import goat from '../assets/goat.png';

// Reusable "Talking Family Tree" QR / watch list (see TFT-QrCode.png).
// Renders "Family" + "Click to watch!" + a row per relative
// (**what baby calls them** · relationship) that plays their video on tap.
//
// Reuse:
//   - Public QR page  → <QrFamilyTree members={...} loading error />
//   - In-app preview  → <QrFamilyTree members={...} onBack={...} /> (adds a Back row)
//   - Custom handling → pass onSelect(member) to intercept the tap.
const QrFamilyTree = ({ members, loading, error, onBack, onSelect }) => {
  const [active, setActive] = useState(null);
  const [showEmpty, setShowEmpty] = useState(null);

  const handleSelect = (member) => {
    if (onSelect) { onSelect(member); return; }
    if (member.videoUrl) setActive(member);
    else {
      setShowEmpty(true)
    }
  };

  if (showEmpty) {
    return <Flex flexDirection="column" justifyContent="center" px={3} py={4} width="100%"   maxWidth={440} >
      <Text
        as="h1"
        fontFamily="Bitter"
        fontWeight={500}
        fontSize="26px"
        color={FT_COLORS.text}
        textAlign="center"
        mb={1}
      >
        No video to show
      </Text>
      <Text fontFamily="Poppins" fontSize="14px" color="#64748B" textAlign="center" mb={3}>
        A video message hasn't been added for this family member.
      </Text>
      <Image src={goat} width={230} m="auto" />
      <Flex px={3} py={3} m="auto" justifyContent="center" >
        <Box
          as="button"
          type="button"
          onClick={() => setShowEmpty(false)}
          sx={{
            background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex',
            alignItems: 'center', gap: '4px', color: FT_COLORS.text, fontFamily: 'Poppins', fontSize: '14px',
          }}
        >
          <FiChevronLeft size={16} /> Back to family
        </Box>
      </Flex>
    </Flex>
  }

  return (
    <Flex flexDirection="column" width="100%" maxWidth={440} css="min-height: 100%; flex: 1 1 auto;">
      <Box flex="1 1 auto" px={3} py={4}>
        <Text
          as="h1"
          fontFamily="Bitter"
          fontWeight={500}
          fontSize="26px"
          color={FT_COLORS.text}
          textAlign="center"
          mb={1}
        >
          Family
        </Text>
        <Text fontFamily="Poppins" fontSize="14px" color={FT_COLORS.muted} textAlign="center" mb={3}>
          Click to watch!
        </Text>

        {loading && <Loading local />}

        {error && (
          <Text fontFamily="Poppins" fontSize="14px" color={FT_COLORS.muted} textAlign="center" py={3}>
            This family tree is no longer available.
          </Text>
        )}

        {!loading && !error && members.map((member) => {
          const watchable = !!member.videoUrl
          return (
            <Flex
              as="button"
              type="button"
              key={member.id}
              onClick={() => handleSelect(member)}
              alignItems="center"
              width="100%"
              bg="white"
              p={3}
              mb={2}
              sx={{
                border: '1px solid',
                borderColor: FT_COLORS.rowBorder,
                borderRadius: '12px',
                boxShadow: 'small',
                cursor: 'pointer',
                gap: '8px',
                textAlign: 'left',
              }}
            >
              <Box flex="1 1 auto" css="min-width: 0;">
                <Text
                  as="span"
                  fontFamily="Poppins"
                  fontWeight={600}
                  fontSize="15px"
                  color={FT_COLORS.text}
                >
                  {member.personalName || member.relationship}
                </Text>
                {member.personalName && member.relationship && (
                  <Text as="span" fontFamily="Poppins" fontWeight={400} fontSize="15px" color={FT_COLORS.primary}>
                    {`  ·  ${member.relationship}`}
                  </Text>
                )}
              </Box>
              <Box as={FaChevronRight} size={14} color={FT_COLORS.text} css="flex: 0 0 auto;" />
            </Flex>
          );
        })}
      </Box>

      {onBack && (
        <Flex px={3} py={3} sx={{ borderTop: '1px solid', borderColor: FT_COLORS.rowBorder }}>
          <Box
            as="button"
            type="button"
            onClick={onBack}
            sx={{
              background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex',
              alignItems: 'center', gap: '4px', color: FT_COLORS.text, fontFamily: 'Poppins', fontSize: '14px',
            }}
          >
            <FiChevronLeft size={16} /> Back
          </Box>
        </Flex>
      )}

      {active && (
        <Flex
          onClick={() => setActive(null)}
          sx={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000,
            bg: 'rgba(0,0,0,0.85)', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Box css="position: absolute; top: 16px; right: 16px;" color="white">
            <FiX size={28} />
          </Box>
          <Box width="100vw" height="100vh" onClick={(e) => e.stopPropagation()}>
            <ReactPlayer url={active.videoUrl} controls playing width="100%" height="100%" />
            <Text color="white" textAlign="center" mt={2} fontFamily="Poppins">
              {[active.personalName, active.relationship].filter(Boolean).join(' · ')}
            </Text>
          </Box>
        </Flex>
      )}
    </Flex>
  );
};

QrFamilyTree.propTypes = {
  members: PropTypes.arrayOf(PropTypes.shape({})),
  loading: PropTypes.bool,
  error: PropTypes.any,
  onBack: PropTypes.func,
  onSelect: PropTypes.func,
};

QrFamilyTree.defaultProps = {
  members: [],
  loading: false,
  error: null,
  onBack: null,
  onSelect: null,
};

export default QrFamilyTree;
